import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MessageSquare, Loader2, ShoppingBag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'bg-yellow-500/15 text-[#FCD34D] border-yellow-500/20',
  in_progress: 'bg-blue-500/15 text-[#93C5FD] border-blue-500/20',
  completed: 'bg-green-500/15 text-[#86EFAC] border-green-500/20',
  shipped: 'bg-purple-500/15 text-[#C4B5FD] border-purple-500/20',
  delivered: 'bg-purple-500/15 text-[#C4B5FD] border-purple-500/20',
  paid: 'bg-blue-500/15 text-[#93C5FD] border-blue-500/20',
  booked: 'bg-yellow-500/15 text-[#FCD34D] border-yellow-500/20',
};

/** "My Purchases" — vendor's own bookings as a buyer (i.e. when they hire
 *  another vendor). Lives inside the vendor dashboard so vendors don't have
 *  to bounce between two dashboards. */
export default function MyPurchases() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/bookings/my-bookings?role=consumer`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (res.ok) setBookings(await res.json());
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="my-purchases">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne">My Purchases</h1>
        <p className="text-sm text-[#9CA3AF] mt-1">
          Services and products you've booked from other Indigenous vendors. Support the community →
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[#A78BFA]" /></div>
      ) : bookings.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center">
          <ShoppingBag size={32} className="mx-auto text-[#9CA3AF] mb-3" />
          <p className="text-white font-semibold">No purchases yet</p>
          <p className="text-[#9CA3AF] text-sm mt-1">When you book another vendor, your orders will show up here.</p>
          <Link to="/browse" className="inline-block mt-4 px-4 py-2 rounded-full bg-yellow-400 text-black text-xs font-bold" data-testid="my-purchases-browse">
            Browse vendors →
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div
              key={b.booking_id}
              className="rounded-2xl bg-white/5 border border-white/10 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              data-testid={`my-purchase-${b.booking_id}`}
            >
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6]/40 to-[#3B82F6]/40 flex items-center justify-center flex-shrink-0">
                  <Calendar size={18} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{b.service_title || 'Service'}</p>
                  <p className="text-[#9CA3AF] text-xs truncate">
                    From <span className="text-[#C4B5FD]">{b.vendor_name || 'Vendor'}</span>
                    {b.created_at && ` · ${new Date(b.created_at).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {b.price !== undefined && (
                  <span className="text-yellow-400 font-bold text-sm">${b.price}</span>
                )}
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border ${STATUS_COLORS[b.status] || STATUS_COLORS.pending}`}>
                  {(b.status || 'pending').replace(/_/g, ' ')}
                </span>
                {b.vendor_id && (
                  <Link
                    to={`/messages/${b.vendor_id}`}
                    className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:text-white hover:bg-white/10 text-xs flex items-center gap-1"
                    data-testid={`my-purchase-message-${b.booking_id}`}
                  >
                    <MessageSquare size={11} /> Message
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
